import { CompanionDecodeResult, CustomCtaCreative } from './CompanionTypes';

const DEFAULT_CTA_TEXT = 'Learn more';
const CTA_BG_ALIASES = ['ctaBgColor', 'cta_bg_color', 'ctaColour'];

/** Parameter names that mean the same thing. Present aliases must agree. */
const ALIASES: string[][] = [
  ['icon', 'Icon'],
  ['campaign', 'campaign name'],
  CTA_BG_ALIASES,
  ['ctaTextColor', 'cta_text_color'],
];

/** Thrown for parameters that must not render; its message becomes the reported reason. */
class Rejection extends Error {}

export function isHttpUrl(value: string | undefined): value is string {
  if (!value) {
    return false;
  }
  try {
    const protocol = new URL(value).protocol;
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/** Accepts `#RRGGBB` or an opaque `#FFRRGGBB`; anything else is ignored. */
export function normalizeColor(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  if (/^#[0-9a-fA-F]{6}$/.test(value)) {
    return value.toUpperCase();
  }
  const argb = /^#[fF]{2}([0-9a-fA-F]{6})$/.exec(value);
  return argb ? `#${argb[1].toUpperCase()}` : undefined;
}

/** Splits `key=value,key=value` into a map, rejecting malformed segments, duplicate keys and conflicting aliases. */
function parse(raw: string): Map<string, string> {
  const params = new Map<string, string>();
  for (const segment of raw.split(',')) {
    const separator = segment.indexOf('=');
    const rawKey = separator < 0 ? '' : segment.slice(0, separator).trim();
    const key = normalizeParameterKey(rawKey);
    if (!key) {
      throw new Rejection('malformed_segment');
    }
    if (params.has(key)) {
      throw new Rejection(`duplicate_key:${key}`);
    }
    params.set(key, segment.slice(separator + 1).trim());
  }
  for (const group of ALIASES) {
    const present = group.filter(name => params.has(name)).map(name => params.get(name));
    if (new Set(present).size > 1) {
      throw new Rejection(`conflicting_aliases:${group[0]}`);
    }
  }
  return params;
}

/** Returns the first alias that is present. */
function pick(params: Map<string, string>, ...names: string[]): string | undefined {
  for (const name of names) {
    if (params.has(name)) {
      return params.get(name);
    }
  }
  return undefined;
}

/** Validates the required fields before constructing a CTA. */
function decodeCta(params: Map<string, string>, campaign?: string): CustomCtaCreative {
  const iconUrl = pick(params, 'icon', 'Icon');
  const title = params.get('bannerTitle');
  const ctaUrl = params.get('ctaUrl');
  if (!isHttpUrl(iconUrl) || !title || !ctaUrl) {
    throw new Rejection('missing_required_field');
  }
  return {
    campaign,
    iconUrl,
    title,
    description: params.get('bannerDescription') || undefined,
    ctaText: params.get('ctaText') || DEFAULT_CTA_TEXT,
    ctaUrl,
    surfaceColor: normalizeColor(params.get('bgColour')),
    textColor: normalizeColor(params.get('bannerTextColor')),
    ctaBgColor: normalizeColor(pick(params, ...CTA_BG_ALIASES)),
    ctaTextColor: normalizeColor(pick(params, 'ctaTextColor', 'cta_text_color')),
  };
}

/** Decodes the raw trafficking parameter string of an ad into a custom CTA creative. */
export function decodeCompanion(raw: string | undefined): CompanionDecodeResult {
  if (!raw?.trim()) {
    return {};
  }
  const suppressStandard = hasTakeoverType(raw);
  try {
    const params = parse(raw);
    if (suppressStandard) {
      return { reason: 'unsupported_ad_type', suppressStandard: true };
    }
    const version = params.get('companion_schema_version');
    if (version !== undefined && version !== '1') {
      throw new Rejection('unsupported_schema_version');
    }
    const adType = params.get('ad_type');
    if (adType !== undefined && adType !== 'custom_cta' && adType !== 'custom') {
      throw new Rejection('unsupported_ad_type');
    }
    if (adType === undefined && !['icon', 'bannerTitle', 'ctaUrl'].some(key => params.has(key))) {
      return {};
    }
    const campaign = pick(params, 'campaign', 'campaign name') || undefined;
    return { creative: decodeCta(params, campaign) };
  } catch (error) {
    if (error instanceof Rejection) {
      return suppressStandard ? { reason: error.message, suppressStandard: true } : { reason: error.message };
    }
    throw error;
  }
}

/** Normalize supported parameter names case-insensitively. */
function normalizeParameterKey(key: string): string {
  const names = [
    ...ALIASES.flat(),
    'bannerTitle',
    'ctaUrl',
    'bannerDescription',
    'ctaText',
    'bgColour',
    'bannerTextColor',
    'ad_type',
    'companion_schema_version',
  ];
  const lowerKey = key.toLowerCase();
  return names.find(name => name.toLowerCase() === lowerKey) || lowerKey;
}

/** A takeover must suppress standard companions even if unrelated parameters are malformed. */
function hasTakeoverType(raw: string): boolean {
  return raw.split(',').some(segment => {
    const separator = segment.indexOf('=');
    const key = segment.slice(0, separator).trim();
    return separator >= 0 && key.toLowerCase() === 'ad_type' && segment.slice(separator + 1).trim() === 'webview';
  });
}
